import { mxCell } from 'mxgraph-js';
import HCLTreeMxGraph, { HCLTreeMxGraphJSON } from '../HCLTreeMxGraph/HCLTreeMxGraph';
import Label from '../HCLTreeNode/HCLTreeVertexValue/Label';
import HCLTreeVertex from '../HCLTreeNode/HCLTreeVertex';

/**
 * Base class that specifies methods for encoding and decoding between {@link JSON} and concrete
 * implementations of {@link HCLTreeMxGraph} such as
 * {@link FaultTreeMxGraph}
 * {@link EventTreeMxGraph}
 * {@link BayesianNetworkMxGraph}
 */
class HCLTreeMxGraphDecoder {
  protected readonly graph: HCLTreeMxGraph;

  private readonly json: HCLTreeMxGraphJSON;

  /**
   * @param {HCLTreeMxGraph} graph
   * @param {HCLTreeMxGraphJSON} json
   */
  constructor(graph: HCLTreeMxGraph, json: HCLTreeMxGraphJSON) {
    this.graph = graph;
    this.json = json;
  }

  /**
   * @return {HCLTreeMxGraph}
   */
  getGraph(): HCLTreeMxGraph {
    return this.graph;
  }

  /**
   * @return {HCLTreeMxGraphJSON}
   */
  getJSON(): HCLTreeMxGraphJSON {
    return this.json;
  }

  /**
   * @param {HCLTreeVertex} vertex
   * @return {HCLTreeVertex}
   */
  protected addVertex(vertex: HCLTreeVertex): HCLTreeVertex {
    return this.graph.addVertex(vertex, null, true);
  }

  /**
   * @param {MxGraphType.mxCell} edge
   * @param {MxGraphType.mxCell} source
   * @param {MxGraphType.mxCell} target
   */
  protected addEdge(edge: mxCell, source: mxCell, target: mxCell): void {
    this.graph.addEdge(edge, this.graph.getDefaultParent(), source, target);
  }

  /**
   * @param {MxGraphType.mxCell} edge
   * @param {MxGraphType.mxCell} source
   * @param {MxGraphType.mxCell} target
   */
  protected addUniqueEdge(edge: mxCell, source: mxCell, target: mxCell) {
    if (!source.edges || source.edges.filter(e => e.target === target).length === 0) {
      return this.addEdge(edge, source, target);
    }
    return null;
  }

  /**
   * @param {MxGraphType.mxCell} source
   * @param {MxGraphType.mxCell} target
   * @param {any} value
   * @return {MxGraphType.mxCell}
   */
  protected addEdgeBetween(source: mxCell, target: mxCell, value?: any): mxCell {
    return this.graph.insertEdge(this.graph.getDefaultParent(), null, value, source, target, null);
  }

  decode(): void {
    this.graph.setName(this.json.name);
    this.graph.setLabel(Label.build(this.json.label));
    this.graph.setModeTreeId(this.json.model_tree_id);
  }
}

export default HCLTreeMxGraphDecoder;
